import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';

const { FiCheckCircle, FiLoader, FiXCircle } = FiIcons;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { updateInvoiceStatus } = useInvoice();
  const [status, setStatus] = useState('processing'); // processing, success, error

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      fetch(`/api/get-session-details?session_id=${sessionId}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to fetch session details from server.');
          }
          return res.json();
        })
        .then(data => {
          if (data.invoiceId) {
            updateInvoiceStatus(data.invoiceId, 'paid');
            setStatus('success');
          } else {
            throw new Error(data.error || 'Invoice ID not found in session.');
          }
        })
        .catch(err => {
          console.error("Failed to update invoice status:", err);
          setStatus('error');
        });
    } else {
      // Handle cases where payment was successful but redirection failed or was manual (e.g., from Payment Element)
      // We can't know which invoice was paid without a session_id, so we show a generic success message.
      // The webhook is the backup that will update the status in the backend.
      setStatus('success');
    }
  }, [searchParams, updateInvoiceStatus]);

  return (
    <div className="flex items-center justify-center min-h-full p-6 bg-gray-50">
      <motion.div
        className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
      >
        {status === 'processing' && (
          <>
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-6">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                <SafeIcon icon={FiLoader} className="h-12 w-12 text-blue-600" />
              </motion.div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
            <p className="text-gray-600 mb-8">
              Please wait while we confirm your transaction.
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
              <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-8">
              Thank you for your payment. Your invoice has been marked as paid.
            </p>
          </>
        )}
        {status === 'error' && (
           <>
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
              <SafeIcon icon={FiXCircle} className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirmation Error</h2>
            <p className="text-gray-600 mb-8">
              Your payment was likely successful, but we couldn't automatically update the invoice status. Please check back later or contact support.
            </p>
          </>
        )}
        <Link to="/invoices">
          <motion.button
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Invoices
          </motion.button>
        </Link>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;